import { useEffect } from 'react';
import { Switch, Route } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import * as Sentry from '@sentry/react';

// HoC
import withAuthProtection from 'hoc/withAuthProtection';
import withTermsAndConditionsProtection from 'hoc/withTermsAndConditionsProtection';
import CountryProvider from 'context/Country/Provider';
import { compose } from 'recompose';

// Containers
import Dashboard from './Dashboard';
import Paystubs from './Paystubs';
import Profile from './Profile';
import TaxDocuments from './TaxDocuments';
import SessionManagement from 'components/SessionManagement';
import Admin from './Admin';
import W4s from './W4s';
import TD1s from './Td1s';
import MyFiles from './MyFiles';
import useFeatureFlags from 'hooks/useFeatureFlags';
import useAuthentication from 'hooks/useAuthentication';
import CrewSupport from './CrewSupport';
import Loanout from './Loanout';
import Offer from './Offer';

// Components
import AppBar from 'components/AppBar';
import Loader from 'components/Loader';
import Alert from 'components/CncUI/Alert';
import { Redirect } from 'react-router';

const useStyles = makeStyles(({ breakpoints, palette, spacing }) => ({
  root: {
    width: '100%',
    backgroundColor: palette.cncUI.default.background,
    maxHeight: '100%',
    height: '100%',
    overflowY: 'auto',
    [breakpoints.down('md')]: {
      overflowY: 'auto',
    },
  },
  content: {
    padding: `0 ${spacing(3)}px ${spacing(3)}px`,
    [breakpoints.down('sm')]: {
      padding: `0 ${spacing(2)}px ${spacing(2)}px`,
    },
  },
  fallbackAlert: {
    marginTop: 24,
  },
}));

const FEATURE_FLAG_TD1S = 'MyCnC-Canada-TD1s';

const Home = () => {
  const classes = useStyles();
  const { refresh, isEnabled } = useFeatureFlags();
  const { user } = useAuthentication();
  const isTD1Enabled = isEnabled(FEATURE_FLAG_TD1S);

  useEffect(() => {
    window.onbeforeunload = null;
    if (user.uid) {
      refresh(user.uid);
    }
  }, []);

  if (!user) {
    return <Loader />;
  }

  const fallback = (
    <Alert error id="Home-ErrorBoundaryAlert" className={classes.fallbackAlert}>
      Oops, something went wrong. Please refresh or try again later.
    </Alert>
  );

  return (
    <CountryProvider>
      <div className={classes.root} id="Home">
        <AppBar />
        <div className={classes.content}>
          <Sentry.ErrorBoundary fallback={fallback}>
            <Switch>
              <Route path="/profile" component={Profile} />
              <Route path="/paystubs" exact={true} component={Paystubs} />
              <Route
                path="/taxdocuments"
                exact={true}
                component={TaxDocuments}
              />
              <Route path="/myfiles" component={MyFiles} />
              <Route path="/w4s" component={W4s} />
              <Route path="/td1s" component={isTD1Enabled ? W4s : TD1s} />
              <Route path="/loanout" component={Loanout} />
              <Route path="/admin" component={Admin} />
              <Route path="/crew-support" component={CrewSupport} />
              <Route path="/offer" component={Offer} />
              <Route path="/" exact component={Dashboard} />
              <Redirect push exact to="/" />
            </Switch>
          </Sentry.ErrorBoundary>
          <SessionManagement />
        </div>
      </div>
    </CountryProvider>
  );
};

export default compose(
  withAuthProtection(),
  withTermsAndConditionsProtection,
)(Home);
